import {
  Box,
  Flex,
  Heading,
  Stack,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import type { MultiValue } from 'chakra-react-select';
import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type FormState,
  type UseFormRegister,
  type UseFormWatch,
} from 'react-hook-form';

import { useGetLoggedInUser, useIsRevelioAdmin } from '@revelio/auth';
import {
  type ApiKeyEndpoint,
  type Client,
  type CustomTaxonomyEnum,
  MetadataKey,
  Role,
  type Tab,
} from '@revelio/data-access';
import { Button, FormField, Input, Select, Switch, Text } from '@revelio/ui';

import { FieldGroup } from '../../shared/ui/admin/groups/field/field-group';
import {
  countConfiguredEndpoints,
  parseRequestsPerSecond,
  RequestsPerSecondModal,
} from './requests-per-second-modal';
import {
  defaultTabOptions,
  getMetadataValue,
  type OptionValues,
  type ReportSelectOptions,
  tabOptions,
} from './utils/helpers';

export type TabSelectOption = { label: string; value: Tab; default?: boolean };
export type PipelineSelectOption = {
  label: string;
  value: string;
  default?: boolean;
  preventAdditions?: boolean;
};
export type ReportSelectOption = {
  label: string;
  value: string;
  default?: boolean;
  preventAdditions?: boolean;
  isFixed?: boolean;
};
export type ClientFormValues = {
  active: boolean;
  tabs: TabSelectOption[];
  live: boolean;
  linkup_postings: boolean;
  num_seats: string;
  requests_per_second: Partial<Record<ApiKeyEndpoint, string>>;
  requests_per_month: string;
  client_name: string;
  report_types: ReportSelectOptions;
  custom_taxonomy: { label: string; value: CustomTaxonomyEnum };
};

type Props = {
  clientToEdit?: Client | null;
  reportOptions?: ReportSelectOption[];
  title?: string;
  description?: string;
  register: UseFormRegister<ClientFormValues> | (() => object);
  control?: Control<ClientFormValues, object>;
  watch: UseFormWatch<ClientFormValues>;
  errors?: FormState<ClientFormValues>['errors'];
  actions?: JSX.Element;
  readonly?: boolean;
  /** Renders the schema-driven Data Builder settings section when the
   * deliverables tab is selected. */
  renderDataBuilderConfig?: () => JSX.Element;
};

const ClientForm = ({
  clientToEdit,
  reportOptions,
  title,
  description,
  register,
  control,
  watch,
  errors,
  actions,
  readonly,
  renderDataBuilderConfig,
}: Props) => {
  const { loggedInUser } = useGetLoggedInUser();
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const isClientAdmin = loggedInUser.role === Role.ClientAdmin;
  const shouldShowApiAccess = isRevelioAdmin || isClientAdmin;
  const isApiAccessReadOnly = !isRevelioAdmin || readonly;
  const {
    isOpen: isRequestsPerSecondOpen,
    onOpen: onRequestsPerSecondOpen,
    onClose: onRequestsPerSecondClose,
  } = useDisclosure();

  const isAddingNewClient = clientToEdit === null;

  const hasDeliverablesTab = watch('tabs', [])?.some(
    (tab) => tab?.value === 'deliverables'
  );

  const hasReportsTab = watch('tabs', [])?.some(
    (tab) => tab?.value === 'reports'
  );

  const isTrialClient = !watch('live', clientToEdit?.live ?? false);

  const toast = useToast();

  const onChangeTabs =
    (field: ControllerRenderProps<ClientFormValues, 'tabs'>) =>
    (selectedOptions: MultiValue<OptionValues>) => {
      const selectedLabels = selectedOptions.map((option) => option.label);
      const hasSentiment = selectedLabels.includes('Sentiment');
      const hasSentimentV1 = selectedLabels.includes('Sentiment_V1');

      if (hasSentiment && hasSentimentV1) {
        toast({
          title: 'Tab conflict',
          description:
            'You cannot add Sentiment and Sentiment_V1 at the same time.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }

      field.onChange(selectedOptions);
    };

  return (
    <Stack
      className="client-form"
      justifyContent="space-between"
      spacing="0"
      height="100%"
      key={clientToEdit?.client_name}
    >
      <Box flex="1" minHeight="0" overflowY="auto" px="2" py="2">
        <FieldGroup title={title} description={description}>
          <VStack spacing="2" align="stretch">
            {isRevelioAdmin && !isAddingNewClient && (
              <FormField label="Active">
                <Switch
                  id="active"
                  size="sm"
                  inactiveLabel="Off"
                  label="On"
                  disabled={!isRevelioAdmin || readonly}
                  defaultChecked={clientToEdit?.active}
                  {...register('active')}
                />
              </FormField>
            )}

            {(isRevelioAdmin || clientToEdit?.num_seats !== '0') && (
              <Box mt="2">
                <FormField label="Number of seats">
                  <Input
                    type="number"
                    wrapperStyle={{ maxWidth: 80 }}
                    disabled={!isRevelioAdmin || readonly}
                    defaultValue={clientToEdit?.num_seats || '0'}
                    {...register('num_seats')}
                  />
                </FormField>
              </Box>
            )}

            <Box mt="2">
              <FormField label="Tabs">
                <Controller
                  name="tabs"
                  control={control}
                  defaultValue={
                    !isAddingNewClient && clientToEdit?.tabs?.length
                      ? (clientToEdit.tabs.map((tab) =>
                          tabOptions.find((o) => o.value === tab)
                        ) as TabSelectOption[])
                      : defaultTabOptions
                  }
                  render={({ field }) => (
                    <Select<OptionValues, true>
                      {...field}
                      isDisabled={!isRevelioAdmin || readonly}
                      isClearable={isRevelioAdmin && !readonly}
                      isMulti
                      name="tabs"
                      options={tabOptions}
                      placeholder="Select tabs..."
                      closeMenuOnSelect={false}
                      noOptionsMessage={() => 'No Tabs'}
                      defaultValue={defaultTabOptions}
                      onChange={onChangeTabs(field)}
                    />
                  )}
                />
              </FormField>
            </Box>

            {hasReportsTab && (
              <Stack
                display={readonly ? 'none' : 'inherit'}
                width="100%"
                borderWidth="1px"
                borderRadius="md"
                p="2"
                mt="2"
                boxShadow="sm"
                color="gray.800"
              >
                <Heading as="h5" size="sm">
                  Report Builder Configuration
                </Heading>
                <Box mt="2">
                  <Controller
                    name="report_types"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (isTrialClient) {
                          return true;
                        }

                        if ((value?.length ?? 0) === 0) {
                          return 'At least one report type is required.';
                        }

                        return true;
                      },
                    }}
                    defaultValue={reportOptions?.filter(
                      (option) => option.default
                    )}
                    render={({ field, fieldState }) => {
                      const addableOptions = reportOptions?.filter(
                        (option) =>
                          !option.preventAdditions ||
                          field.value?.some((o) => o.label === option.label)
                      );

                      return (
                        <FormField
                          label="Report Types"
                          errors={
                            fieldState.error?.message
                              ? [fieldState.error.message]
                              : undefined
                          }
                        >
                          <Select<OptionValues, true>
                            {...field}
                            isDisabled={!isRevelioAdmin || readonly}
                            isClearable={
                              isRevelioAdmin &&
                              !readonly &&
                              !addableOptions?.some((o) => o.isFixed)
                            }
                            isMulti
                            isInvalid={!!fieldState.error}
                            name="report_types"
                            options={addableOptions}
                            placeholder="Select report types..."
                            closeMenuOnSelect={false}
                            noOptionsMessage={() => 'No report types'}
                          />
                        </FormField>
                      );
                    }}
                  />
                </Box>
              </Stack>
            )}

            {hasDeliverablesTab && renderDataBuilderConfig?.()}

            <FormField
              label="Data Access"
              supportingText="Bypasses trial end dates and enables full data access for ONE_YEAR_LAG trial clients."
            >
              <Switch
                id="live"
                size="sm"
                inactiveLabel="Trial"
                label="Live"
                disabled={!isRevelioAdmin || readonly}
                defaultChecked={clientToEdit?.live}
                {...register('live')}
              />
            </FormField>

            <FormField label="Website Postings">
              <Switch
                id="linkup_postings"
                size="sm"
                inactiveLabel="Off"
                label="On"
                disabled={true}
                defaultChecked={
                  isAddingNewClient ? false : clientToEdit?.linkup_postings
                }
                {...register('linkup_postings')}
              />
            </FormField>
          </VStack>
        </FieldGroup>

        {shouldShowApiAccess && (
          <Box mt="2">
            <Controller
              name="requests_per_second"
              control={control}
              defaultValue={parseRequestsPerSecond(
                getMetadataValue(
                  clientToEdit?.metadata,
                  MetadataKey.RequestsPerSecond
                )
              )}
              render={({ field: rpsField }) => (
                <Controller
                  name="requests_per_month"
                  control={control}
                  defaultValue={
                    getMetadataValue(
                      clientToEdit?.metadata,
                      MetadataKey.RequestsPerMonth
                    ) ?? ''
                  }
                  render={({ field: rpmField }) => {
                    const configuredCount = countConfiguredEndpoints(
                      rpsField.value ?? {}
                    );
                    const monthlyValue = rpmField.value ?? '';
                    const hasMonthly = monthlyValue.trim() !== '';
                    const isCustomized = configuredCount > 0 || hasMonthly;
                    const summaryText = (() => {
                      const parts: string[] = [];
                      if (configuredCount === 1) {
                        parts.push('1 endpoint customized');
                      } else if (configuredCount > 1) {
                        parts.push(
                          `${configuredCount} endpoints with specific limits`
                        );
                      }
                      if (hasMonthly) {
                        parts.push(`Monthly cap: ${monthlyValue}`);
                      }
                      if (parts.length === 0) {
                        return 'Default rate limits (10/sec, no monthly cap)';
                      }
                      return parts.join(' \u00b7 ');
                    })();

                    return (
                      <FormField label="API Access Limits">
                        <Flex alignItems="center" gap="2" flexWrap="wrap">
                          <Text
                            fontSize="sm"
                            weight={isCustomized ? 'semibold' : 'regular'}
                          >
                            {summaryText}
                          </Text>
                          <Button
                            variant="ghost"
                            color="primary"
                            size="sm"
                            type="button"
                            onClick={onRequestsPerSecondOpen}
                            disabled={isApiAccessReadOnly}
                            style={{
                              height: 'auto',
                              minHeight: 0,
                              padding: '2px 6px',
                            }}
                          >
                            Edit
                          </Button>
                          <RequestsPerSecondModal
                            isOpen={isRequestsPerSecondOpen}
                            onClose={onRequestsPerSecondClose}
                            value={rpsField.value ?? {}}
                            onChange={rpsField.onChange}
                            monthlyValue={monthlyValue}
                            onMonthlyChange={rpmField.onChange}
                            isReadOnly={isApiAccessReadOnly}
                          />
                        </Flex>
                      </FormField>
                    );
                  }}
                />
              )}
            />
          </Box>
        )}
      </Box>

      {actions && (
        <Box
          flexShrink={0}
          borderTopWidth="1px"
          borderColor="gray.200"
          px="2"
          py="2"
        >
          {actions}
        </Box>
      )}
    </Stack>
  );
};

export default ClientForm;
